<template>
  <section id="waitlist" class="waitlist-form">
    <div class="form-container glass-card reveal">
      <div class="form-header">
        <span class="badge">Exclusive Beta</span>
        <h2>Help Us Build Bizzento for You</h2>
        <p class="subtitle">
          Share your challenges and we'll tailor Bizzento to solve them.
        </p>
      </div>

      <Transition name="fade-slide" mode="out-in">
        <form
          v-if="!showSuccess"
          @submit.prevent="handleSubmit"
          id="waitlistForm"
          class="actual-form"
        >
          <div class="form-group reveal delay-1">
            <label for="business">What business do you run? *</label>
            <input
              type="text"
              id="business"
              v-model="formData.business"
              required
              placeholder="e.g., Home bakery, Online boutique"
            />
          </div>

          <div class="form-group reveal delay-2">
            <label for="topQuestion"
              >What's the #1 question you'd want Bizzento to answer? *</label
            >
            <div class="select-wrapper">
              <select id="topQuestion" v-model="formData.topQuestion" required>
                <option value="" disabled>
                  Select the question that matters most
                </option>
                <option value="pricing">Should I raise my prices?</option>
                <option value="profitable">
                  Which products are most profitable?
                </option>
                <option value="hiring">When should I hire help?</option>
                <option value="scale">Am I ready to scale?</option>
                <option value="demand">What are customers asking for?</option>
                <option value="marketing">
                  Where should I focus my marketing?
                </option>
                <option value="other">Other (please specify)</option>
              </select>
            </div>
          </div>

          <Transition name="expand">
            <div class="form-group" v-if="formData.topQuestion === 'other'">
              <label for="otherQuestion">Please specify your question</label>
              <textarea
                id="otherQuestion"
                v-model="formData.otherQuestion"
                :required="formData.topQuestion === 'other'"
                placeholder="What would you most want to know about your business?"
              ></textarea>
            </div>
          </Transition>

          <div class="form-group reveal delay-3">
            <label for="email">Email Address *</label>
            <input
              type="email"
              id="email"
              v-model="formData.email"
              required
              placeholder="name@example.com"
            />
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <button
            type="submit"
            class="submit-button reveal delay-3"
            :disabled="isSubmitting"
          >
            <span v-if="!isSubmitting">Join the Beta Program</span>
            <span v-else class="loader"></span>
          </button>

          <p class="privacy-note">🔒 We value your privacy. No spam, ever.</p>
        </form>

        <div v-else class="success-message">
          <div class="success-icon">✨</div>
          <h3>You're on the list!</h3>
          <p>
            Thank you for joining our journey. We'll be in touch soon to discuss
            how Bizzento can help your business thrive.
          </p>
          <button @click="showSuccess = false" class="back-btn">
            Back to top
          </button>
        </div>
      </Transition>
    </div>
  </section>
</template>
<style scoped>
.waitlist-form {
  --bg-warm-base: #f2eee0;
  --sunlight-glow: #faefca;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #8e8a82;

  padding: 100px 24px;
  background-color: var(--bg-warm-base);
  display: flex;
  justify-content: center;
}

.form-container {
  width: 100%;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  border: 1px solid white;
  padding: 60px 40px;
  border-radius: 40px;
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.05);
}

.form-header {
  text-align: center;
  margin-bottom: 40px;
}

.badge {
  background: var(--sunlight-glow);
  color: #856404;
  padding: 6px 14px;
  border-radius: 100px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
}

h2 {
  font-size: 2.2rem;
  margin: 20px 0 10px;
  font-weight: 800;
}

.subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
}

/* Form Styling */
.form-group {
  margin-bottom: 24px;
  text-align: left;
}

label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 0.95rem;
  color: var(--dark-accent);
}

input,
select,
textarea {
  width: 100%;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: white;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: var(--soft-gold);
  box-shadow: 0 0 0 4px var(--sunlight-glow);
}

textarea {
  min-height: 100px;
  resize: vertical;
}

.submit-button {
  width: 100%;
  background: var(--dark-accent);
  color: white;
  padding: 18px;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s;
  margin-top: 10px;
}

.submit-button:hover {
  transform: translateY(-2px);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.privacy-note {
  margin-top: 20px;
  font-size: 0.8rem;
  color: var(--text-muted);
  text-align: center;
}

/* Success State */
.success-message {
  text-align: center;
  padding: 40px 0;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 20px;
}

.success-message h3 {
  font-size: 2rem;
  margin-bottom: 16px;
}

.back-btn {
  margin-top: 30px;
  background: transparent;
  border: 1px solid var(--soft-gold);
  padding: 10px 20px;
  border-radius: 100px;
  cursor: pointer;
}

/* Animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 200px;
  overflow: hidden;
}
.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.error-message {
  background: #fff5f5;
  color: #c53030;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 0.9rem;
  border-left: 4px solid #c53030;
}

@media (max-width: 600px) {
  .form-container {
    padding: 40px 24px;
    border-radius: 0;
  }
  .waitlist-form {
    padding: 0;
  } /* Full width on mobile */
}
</style>

<script setup>
import { ref, reactive, nextTick } from 'vue';

// Configuration - update with your actual API endpoint
const API_ENDPOINT = 'https://services.mviaso.com/api/email/waitlist';

const formData = reactive({
  business: '',
  topQuestion: '',
  otherQuestion: '',
  email: '',
});

const isSubmitting = ref(false);
const showSuccess = ref(false);
const errorMessage = ref('');

const handleSubmit = async () => {
  isSubmitting.value = true;
  errorMessage.value = '';

  // Prepare submission data
  const submitData = {
    business: formData.business,
    topQuestion: formData.topQuestion,
    otherQuestion:
      formData.topQuestion === 'other' ? formData.otherQuestion : '',
    email: formData.email,
    timestamp: new Date().toISOString(),
  };

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    });

    if (!response.ok) {
      // Handle HTTP errors
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Server error: ${response.status}`);
    }

    const result = await response.json();
    console.log('Form submitted successfully:', result);

    // Show success message
    showSuccess.value = true;

    // Reset form
    formData.business = '';
    formData.topQuestion = '';
    formData.otherQuestion = '';
    formData.email = '';

    // Scroll to success message
    await nextTick();
    document.querySelector('.success-message')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  } catch (error) {
    console.error('Form submission error:', error);
    errorMessage.value =
      error.message || 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
};
</script>
